/**
 * UserBooksContext — fonte única de verdade para todos os livros do usuário.
 *
 * Problema resolvido: anteriormente, Home.tsx abria 5 assinaturas Firestore
 * separadas (useUserBooks × 5), causando race conditions entre contadores da
 * mesma tela. Agora existe UMA única assinatura onSnapshot; todas as views
 * filtradas (readingNow, readBooks, etc.) são derivadas de forma síncrona.
 */

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
  type ReactNode,
} from "react";
import type { DocumentData, QueryDocumentSnapshot } from "firebase/firestore";
import {
  loadMoreUserBooks,
  SHELF_PAGE_SIZE,
  subscribeToUserBooksFirstPage,
  type BookStatus,
  type UserBook,
} from "../services/firebase/booksService";
import { useAuth } from "../hooks/useAuth";
import { isBookOwned } from "../features/books/utils/bookOwnership";
import {
  shelfRevision,
  useBooksFinishedInYear,
  useShelfCounts,
} from "../features/books/hooks/useBooksFinishedInYear";
import { computeShelfTileStats } from "../features/books/utils/shelfTiles";
import { countBooksFinishedInYear, currentYearLocal } from "../features/books/utils/yearlyGoal";
import { toMs } from "../utils/timestampUtils";

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------

export interface UserBooksContextData {
  /** Todos os livros do usuário (sem filtro) */
  allBooks:       UserBook[];
  readingNow:     UserBook[];
  wantToRead:     UserBook[];
  /** Livros com status "read", ordenados por finishedAt desc */
  readBooks:      UserBook[];
  /**
   * Livros que o usuário POSSUI — eixo `ownership`, não `status`. É a estante
   * "Tenho", e portanto um SUPERSET: inclui os que ele está lendo, já leu ou
   * quer ler e também tem. Os quatro recortes não somam mais `allBooks`.
   */
  ownedBooks:     UserBook[];
  /**
   * Só os que ele tem SEM intenção de leitura declarada (`status: "owned"`).
   * Recorte estreito, para superfícies onde o superset apareceria duplicado —
   * a faixa da Home, que existe justamente porque esses livros não aparecem em
   * nenhuma outra (os demais já têm a faixa de "Lendo" e a da fila).
   */
  ownedOnlyBooks: UserBook[];
  isLoading:      boolean;
  /** Contagem live — nunca lê stats.totalBooksRead do Firestore (evita stale) */
  totalBooksRead: number;
  /**
   * Numerador da meta anual — livros com `status: "read"` e `finishedAt` no ano
   * corrente. **Contado no servidor** (`count()`), então não depende de quantos
   * livros a janela paginada carregou. Ver `services/firebase/shelfCounts.ts`.
   *
   * ⚠️ Nunca troque por `user_stats.totalBooksRead`: aquele é vitalício e era
   * o que fazia "Meta 2026" nascer batida com livro lido em 2023.
   * `tests/components/metaParidadeSuperficies.test.ts` trava essa regra.
   */
  booksFinishedThisYear: number;
  /**
   * As quatro contagens da estante, **contadas no servidor** — abas do seletor
   * e tiles do Perfil.
   *
   * ⚠️ Os quatro NÃO somam `allBooks.length`: três recortam o ciclo de leitura
   * (`status`) e o quarto recorta POSSE, que é eixo independente e superset.
   * Ver `countShelfByStatus`.
   */
  shelfCounts: Record<BookStatus, number>;
  /** Ano corrente, local — mesmo frame da divisória de ano da estante. */
  currentYear: number;
  /** Últimas 3 capas de livros lidos (data para html-to-image) */
  recentCovers:   string[];
  /** Carrega próximo lote da estante sob demanda. */
  loadMore: () => Promise<void>;
  hasMore: boolean;
  isLoadingMore: boolean;
}

// ---------------------------------------------------------------------------
// Contexto
// ---------------------------------------------------------------------------

const UserBooksContext = createContext<UserBooksContextData | null>(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function UserBooksProvider({ children }: { children: ReactNode }) {
  // ⚠️ A janela é a MESMA das superfícies públicas (`fetchUserBooksOnce`), e
  // por isso é uma constante importada e não um literal daqui: quando o fix do
  // BUG-76 levantou só esta, a estante pública ficou em 100 e passou a listar 3
  // livros de 2026 embaixo de uma meta que dizia 8. Ver `SHELF_PAGE_SIZE`.
  const PAGE_SIZE = SHELF_PAGE_SIZE;
  const { user } = useAuth();
  const [allBooks,  setAllBooks]  = useState<UserBook[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [lastDoc, setLastDoc] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);

  useEffect(() => {
    if (!user?.uid) {
      setAllBooks([]);
      setIsLoading(false);
      setHasMore(false);
      setLastDoc(null);
      return;
    }

    setIsLoading(true);
    const unsub = subscribeToUserBooksFirstPage(
      user.uid,
      PAGE_SIZE,
      ({ books, lastDoc: nextLastDoc }) => {
        setAllBooks(books);
        setLastDoc(nextLastDoc);
        setHasMore(books.length >= PAGE_SIZE && !!nextLastDoc);
        setIsLoading(false);
      },
      (err) => {
        console.error("[Lura] UserBooksContext error:", err.message);
        setIsLoading(false);
      }
    );

    return unsub;
  }, [PAGE_SIZE, user?.uid]);

  const loadMore = useCallback(async () => {
    if (!user?.uid || !lastDoc || isLoadingMore || !hasMore) return;
    setIsLoadingMore(true);
    try {
      const page = await loadMoreUserBooks(user.uid, PAGE_SIZE, lastDoc);
      setAllBooks((prev) => {
        const map = new Map(prev.map((b) => [b.id, b]));
        for (const book of page.books) map.set(book.id, book);
        return [...map.values()];
      });
      setLastDoc(page.lastDoc);
      setHasMore(page.books.length >= PAGE_SIZE && !!page.lastDoc);
    } catch (err) {
      console.error("[Lura] UserBooksContext loadMore error:", err);
    } finally {
      setIsLoadingMore(false);
    }
  }, [PAGE_SIZE, hasMore, isLoadingMore, lastDoc, user?.uid]);

  // Meta anual — contada no servidor, e por isso imune ao tamanho da janela
  // acima. Mora AQUI, e não em cada tela, porque Home, `/shelf/read` e Perfil
  // pedem o mesmo número: três hooks iguais seriam três `count()` idênticos por
  // navegação. É a mesma razão de o contexto ser subscription única.
  //
  // O fallback client-side segue calculado: é o que a tela mostra enquanto a
  // contagem está em voo, e o que sobra se ela falhar (offline). Pode
  // subcontar — é exatamente o número de antes desta mudança, nunca pior.
  const currentYear = currentYearLocal();
  const finishedFallback = useMemo(
    () => countBooksFinishedInYear(allBooks, currentYear),
    [allBooks, currentYear],
  );
  const revision = useMemo(() => shelfRevision(allBooks), [allBooks]);
  const booksFinishedThisYear = useBooksFinishedInYear({
    userId: user?.uid,
    year: currentYear,
    fallback: finishedFallback,
    revision,
  });

  // As quatro contagens, pelo mesmo motivo e com o mesmo contrato: elas
  // aparecem nas abas da estante E nos tiles do Perfil, e derivá-las da janela
  // fazia "Lido 39" embaixo de uma meta que dizia 8.
  const countsFallback = useMemo(() => computeShelfTileStats(allBooks).shelfCounts, [allBooks]);
  const shelfCounts = useShelfCounts({
    userId: user?.uid,
    fallback: countsFallback,
    revision,
  });

  const value = useMemo<UserBooksContextData>(() => {
    const readBooks  = allBooks.filter((b) => b.status === "read");
    const readingNow = allBooks.filter((b) => b.status === "reading");
    const wantToRead = allBooks.filter((b) => b.status === "want_to_read");
    // `isBookOwned` e não `status === "owned"`: posse é eixo próprio desde a
    // refatoração dos status, e tolera o doc legado que ainda não tem o campo.
    const ownedBooks = allBooks.filter((b) => isBookOwned(b));
    const ownedOnlyBooks = allBooks.filter((b) => b.status === "owned");

    const sortedWithCover = [...readBooks]
      .filter((b) => b.coverUrl)
      .sort(
        (a, b) =>
          (toMs(b.finishedAt) || toMs(b.updatedAt)) -
          (toMs(a.finishedAt) || toMs(a.updatedAt)),
      );

    const seenBookKeys = new Set<string>();
    const uniqueRecent: UserBook[] = [];
    for (const b of sortedWithCover) {
      const key = (b.bookId?.trim() || b.id).trim() || b.coverUrl;
      if (seenBookKeys.has(key)) continue;
      seenBookKeys.add(key);
      uniqueRecent.push(b);
      if (uniqueRecent.length >= 3) break;
    }
    const recentCovers = uniqueRecent.map((b) => b.coverUrl);

    return {
      allBooks,
      readBooks,
      readingNow,
      wantToRead,
      ownedBooks,
      ownedOnlyBooks,
      isLoading,
      totalBooksRead: readBooks.length,
      booksFinishedThisYear,
      shelfCounts,
      currentYear,
      recentCovers,
      loadMore,
      hasMore,
      isLoadingMore,
    };
  }, [
    allBooks,
    booksFinishedThisYear,
    shelfCounts,
    currentYear,
    hasMore,
    isLoading,
    isLoadingMore,
    loadMore,
  ]);

  return (
    <UserBooksContext.Provider value={value}>
      {children}
    </UserBooksContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hook público
// ---------------------------------------------------------------------------

/**
 * A estante quando ela EXISTE — `null` fora do provider, em vez de lançar.
 * Para superfícies de enfeite que não podem derrubar a página por falta dela
 * (a prévia do perfil na `/pro`, montada sem provider em testes e harness).
 */
export function useOptionalUserBooksContext(): UserBooksContextData | null {
  return useContext(UserBooksContext);
}

export function useUserBooksContext(): UserBooksContextData {
  const ctx = useContext(UserBooksContext);
  if (!ctx) throw new Error("useUserBooksContext requer UserBooksProvider como ancestral");
  return ctx;
}
